import { memo, useState } from 'react'

import { Check, Edit } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { useUserInfoUpdateMutation } from 'hooks/mutations/identity/User'

/** E.164: a plus, a non-zero country digit, then up to fourteen more. */
const E164 = /^\+[1-9]\d{1,14}$/

/** Squares the buttons off against the field they sit next to — a small outlined input is 40px tall. */
const matchFieldHeight = { height: 40 }

export type PhoneNumberFieldProps = {
  phoneNumber?: string
  verified?: boolean
}

/**
 * The one editable claim on this panel. Kept inline rather than behind a dialog — it is a single
 * field, and the profile is otherwise a read-out.
 */
const PhoneNumberFieldRaw = ({ phoneNumber, verified }: PhoneNumberFieldProps) => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')

  const { mutate: updateUserInfo, isPending } = useUserInfoUpdateMutation()

  const trimmed = value.trim()
  // Empty is allowed: it clears the number. Anything else has to be dialable.
  const invalid = trimmed.length > 0 && !E164.test(trimmed)

  const startEditing = () => {
    setValue(phoneNumber ?? '')
    setEditing(true)
  }

  const submit = () => {
    if (invalid) return

    updateUserInfo(
      // An empty string clears it; null would mean "leave whatever is stored".
      { userId: 'me', userInfoEdit: { phoneNumber: trimmed } },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:_Navigation.Settings.Profile.phoneNumberSaved'), { variant: 'success' })
          setEditing(false)
        },
        // Failures are left to the global handler, which surfaces the API's own reason.
      },
    )
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="heading-xs">{t('domain:_Navigation.Settings.Profile.phoneNumber')}</Typography>

      {editing ? (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 0.5 }}>
          <TextField
            size="small"
            autoFocus
            fullWidth
            type="tel"
            value={value}
            error={invalid}
            helperText={invalid ? t('domain:_Navigation.Settings.Profile.phoneNumberInvalid') : undefined}
            placeholder={t('domain:_Navigation.Settings.Profile.phoneNumberPlaceholder')}
            disabled={isPending}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                submit()
              }
            }}
          />
          <LoadingButton
            variant="text"
            size="small"
            sx={matchFieldHeight}
            loading={isPending}
            disabled={invalid}
            onClick={submit}
          >
            {t('save')}
          </LoadingButton>
          <Button
            variant="text"
            size="small"
            color="inherit"
            sx={matchFieldHeight}
            disabled={isPending}
            onClick={() => setEditing(false)}
          >
            {t('cancel')}
          </Button>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
          <Typography variant="text" sx={[!phoneNumber && { color: 'text.secondary' }]}>
            {phoneNumber || t('domain:_Navigation.Settings.Profile.phoneNumberNotSet')}
          </Typography>
          {!!phoneNumber && verified && (
            <Tooltip title={t('domain:_Navigation.Settings.Profile.phoneNumberVerified')}>
              <Check fontSize="small" sx={{ color: (theme) => theme.palette.success[theme.palette.mode] }} />
            </Tooltip>
          )}
          <Button variant="text" size="small" startIcon={<Edit />} onClick={startEditing}>
            {t('edit')}
          </Button>
        </Stack>
      )}
    </Box>
  )
}

export const PhoneNumberField = memo(PhoneNumberFieldRaw)
